import { Form, FormInstance } from 'antd';
import { FormProviderProps } from 'antd/es/form/context';
import dayjs, { Dayjs } from 'dayjs';
import { PropsWithChildren, createContext, useContext, useState } from 'react';

import { DateSelectionTypeEnum } from 'ui/form/temporality-select-input';

import { defaultFilters, getDefaultFilters } from './constants';
import {
  INDICATOR_DISPLAY_TYPES_MAP,
  INDICATOR_GROUP_BY_TYPES_MAP,
  IndicatorAnalyzeFilters,
  IndicatorAnalyzeRangeFilters,
} from './types';

const FILTER_FORM_NAME = 'indicator-analyze-filters-form';

type Form = NonNullable<FormProviderProps['onFormChange']>;

const updateDateRange = (
  form: FormInstance,
  noDefaultFor: DateSelectionTypeEnum[] | undefined,
): [null, null] | [Dayjs, Dayjs] => {
  return form.getFieldValue('filterType') === 'date' &&
    // Check for `period` included in noDefaultFor to empty the input when
    // it is changed back to date filter type from ring filter type
    noDefaultFor?.includes(DateSelectionTypeEnum.Period)
    ? [null, null]
    : [dayjs().subtract(1, 'week'), dayjs()];
};

const IndicatorAnalyzeFiltersContext = createContext<IndicatorAnalyzeFilters>({
  filters: defaultFilters,
});

const IndicatorAnalyzeFiltersProvider = ({
  children,
  noDefaultFor,
}: PropsWithChildren<{ noDefaultFor?: DateSelectionTypeEnum[] }>) => {
  const [filters, setFilters] = useState(getDefaultFilters);

  const onFormChange: FormProviderProps['onFormChange'] = (
    formName,
    { forms },
  ) => {
    if (formName === FILTER_FORM_NAME) {
      const form = forms[formName];

      const dateRange = updateDateRange(form as FormInstance, noDefaultFor);

      const values = {
        dateRange,
        ringRange: [null, null],
        groupByType: INDICATOR_GROUP_BY_TYPES_MAP.day,
        displayType: INDICATOR_DISPLAY_TYPES_MAP.bar,
        ...form.getFieldsValue(),
      } satisfies IndicatorAnalyzeRangeFilters;

      // Setting default range for both day and ring because Antd Form remove the fields value if the input is
      // not rendered. Since we conditionnaly render the dateRange or ringRange depending on the `filterType`
      // it is required to add them with defaut value to the FiltersProvider. Therefore, we're sure it is always
      // defined when the filterType is changed
      setFilters(values);
      form.setFieldsValue(values);
    }
  };

  return (
    <IndicatorAnalyzeFiltersContext.Provider value={{ filters }}>
      <Form.Provider onFormChange={onFormChange}>{children}</Form.Provider>
    </IndicatorAnalyzeFiltersContext.Provider>
  );
};

const useIndicatorAnalyzeFiltersFormName = () => FILTER_FORM_NAME;

const useIndicatorAnalyzeFilters = () =>
  useContext(IndicatorAnalyzeFiltersContext).filters;

export {
  IndicatorAnalyzeFiltersProvider,
  useIndicatorAnalyzeFiltersFormName,
  useIndicatorAnalyzeFilters,
};
